/**
 * useTaskListKpis - Hook para buscar KPIs totalizadores da listagem de tarefas
 * 
 * Consome a RPC `get_task_list_kpis` que retorna contagens por status
 * respeitando permissões granulares por fase via helper `can_user_view_task`.
 * 
 * @version 1.0.0
 * RELEASE: 2026-04-08 v1.0.0 - Criação inicial
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';
import type { TaskListFilters } from '@/hooks/useTasksPaginated';
import { useTaskModuleType } from '@/hooks/useTaskModuleType';

/** Logger do módulo */
const logger = createLogger('useTaskListKpis');

/** Estrutura dos KPIs retornados pela RPC */
export interface TaskListKpis {
  total_tasks: number;
  created_today: number;
  in_progress: number;
  paused: number;
  completed: number;
  /** v1.6.0: Tarefas pendentes (não iniciadas) */
  pending: number;
}

/** Valores padrão (zerados) */
const EMPTY_KPIS: TaskListKpis = {
  total_tasks: 0,
  created_today: 0,
  in_progress: 0,
  paused: 0,
  completed: 0,
  pending: 0,
};

/**
 * Hook para buscar KPIs totalizadores da listagem de tarefas
 * Retorna contagens por status com permissões granulares por fase
 * 
 * @param filters - Filtros ativos da listagem (sincroniza KPIs com tabela)
 */
export function useTaskListKpis(filters?: TaskListFilters) {
  const { currentTenant } = useAuth();
  // Módulo corrente (normal | retention | legal) derivado da rota
  const taskType = useTaskModuleType();

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['task-list-kpis', currentTenant?.id, filters, taskType],
    queryFn: async () => {
      logger.debug('Buscando KPIs da listagem de tarefas', { filters });

      const { data, error } = await supabase.rpc('get_task_list_kpis', {
        p_search: filters?.search || null,
        p_category_id: filters?.category_id || null,
        p_classification_id: filters?.classification_id || null,
        p_phase_id: filters?.phase_id || null,
        p_status_filter: filters?.status || null,
        p_priority_filter: filters?.priority || null,
        p_assigned_to_filter: filters?.assigned_to || null,
        p_date_from: filters?.date_from || null,
        p_date_to: filters?.date_to || null,
        p_created_by_filter: filters?.created_by || null,
        p_due_date_from: filters?.due_date_from || null,
        p_due_date_to: filters?.due_date_to || null,
        p_completed_from: filters?.completed_from || null,
        p_completed_to: filters?.completed_to || null,
        p_tenant_hint: currentTenant?.id || null,
        // v1.7.0: filtro "Sem Prazo" / "Com Prazo"
        p_due_date_is_null:
          typeof filters?.due_date_is_null === 'boolean' ? filters!.due_date_is_null : null,
        // Escopo do módulo corrente (Tarefas / Retenção / Jurídico)
        p_task_type: taskType,
      } as any);

      if (error) {
        logger.error('Erro ao buscar KPIs de tarefas', error);
        throw error;
      }

      // RPC retorna array com 1 linha
      const row = Array.isArray(data) ? data[0] : data;
      if (!row) return EMPTY_KPIS;

      return {
        total_tasks: Number(row.total_tasks) || 0,
        created_today: Number(row.created_today) || 0,
        in_progress: Number(row.in_progress) || 0,
        paused: Number(row.paused) || 0,
        completed: Number(row.completed) || 0,
        pending: Number(row.pending) || 0,
      } as TaskListKpis;
    },
    enabled: !!currentTenant?.id,
    staleTime: 15_000,
    gcTime: 2 * 60_000,
  });

  return {
    kpis: data || EMPTY_KPIS,
    isLoading,
    refetch,
  };
}
